import { type RefObject, useCallback, useRef, useState } from "react";
import { seedPresetSettings } from "./detailedPresetState";
import type { PresetSettings, PrintoutPreset } from "./printoutPresets";

/** The saved printout being edited, with its on-screen settings. */
export type LivePreset = { presetId: string; settings: PresetSettings };

/** This session's latest settings per printout — newer than the cache while a save is on its way. */
export type SessionEdits = Map<string, PresetSettings>;

export type LivePresetState = {
	live: LivePreset | null;
	/** `live` as of right now, for callbacks that run before the next render. */
	liveRef: RefObject<LivePreset | null>;
	/** Puts new on-screen settings in place (ref at once, state for the next render). */
	replaceLive: (next: LivePreset) => void;
	sessionEditsRef: RefObject<SessionEdits>;
};

/**
 * The on-screen settings of the saved printout in use. Seeded from a printout only when it becomes the
 * one in use (or first loads) — during render, so no frame paints the previous printout's settings under
 * the new one's name (React's "adjusting state when a prop changes" pattern). A save answering with the
 * server's copy updates the cache but never this: reseeding then would throw away whatever the user
 * changed while the save was on its way.
 */
export const useLivePreset = (activePreset: PrintoutPreset | null): LivePresetState => {
	const sessionEditsRef = useRef<SessionEdits>(new Map());
	const [live, setLive] = useState<LivePreset | null>(null);
	const activePresetId = activePreset ? activePreset.id : null;
	if ((live ? live.presetId : null) !== activePresetId) {
		setLive(activePreset ? { presetId: activePreset.id, settings: seedPresetSettings(activePreset, sessionEditsRef.current) } : null);
	}
	const liveRef = useRef(live);
	liveRef.current = live;

	const replaceLive = useCallback((next: LivePreset) => {
		liveRef.current = next;
		setLive(next);
	}, []);

	return { live, liveRef, replaceLive, sessionEditsRef };
};
